import { Status, NonClientError, ServerError } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { omit } from 'ramda';
import useFmcHost from 'hooks/useFmcHost';

import useQueryParameters from 'hooks/useQueryParameters';

import { CreditApplication } from 'state/creditApplication';

import { InstantCredit, mergeInstantCredit } from 'state/instantCredit';
import { selectTenant } from 'state/tenant';
import useIovation from 'hooks/useIovation';
import { receiveApplicationResult, ApplicationResultType } from 'fmc-sdk';
import catchOnly from 'utils/catchOnly';
import useAnalyticsVariables from 'hooks/useAnalyticVariables';
import useHandleExistingAccount from './handleExistingAccount';
import { transformDob } from 'utils/dateUtils';

const PATH = '/api/instant_credit';

type InstantCreditHook = () => [
  (params: CreditApplication, reCaptchaToken: string) => Promise<InstantCredit>,
  Status,
  boolean,
];

export class InstantCreditError extends Error {}

const useInstantCredit: InstantCreditHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const {
    mockIC,
    storeNumber: queryStoreNumber,
    token,
    location,
    channel,
    subchannel,
    applicationSubType,
    subId,
    departmentId,
  } = useQueryParameters();
  const dispatch = useDispatch();
  const fmcDispatch = useFmcHost();
  const handleExistingAccount = useHandleExistingAccount();
  const {
    setAnalyticsVariables,
    setAnalyticsVariablesByApplication,
  } = useAnalyticsVariables();

  const { defaultStoreNumber } = useSelector(selectTenant);

  const iovationBlackbox = useIovation();

  const applyForCredit = useCallback(
    async (
      params: CreditApplication,
      reCaptchaToken: string,
    ): Promise<InstantCredit> => {
      setError(false);

      const blackbox = await iovationBlackbox;
      const overrides: Partial<CreditApplication> = {
        storeNumber:
          params.storeNumber || queryStoreNumber || defaultStoreNumber,
      };

      params = transformDob(['dateOfBirth'], params);
      if (params.authBuyer) {
        params = transformDob(['authBuyer', 'dateOfBirth'], params);
      }
      if (params.jointApplicant) {
        params = transformDob(['jointApplicant', 'dateOfBirth'], params);
      }
      if (
        params.jointApplicant &&
        params.jointApplicant.sameAddressAsPrimaryApplicant
      ) {
        params.jointApplicant.address1 = params.address1;
        params.jointApplicant.address2 = params.address2;
        params.jointApplicant.zip = params.zip;
        params.jointApplicant.city = params.city;
        params.jointApplicant.state = params.state;
      }

      try {
        // TODO: Missing params include clientVariable4. I have
        // a question out to Tim to confirm whether these are still needed.

        const response = await fetch(PATH, {
          method: 'POST',
          body: {
            ...omit(['alternativePhone'], params),
            ...overrides,
            altPhone: params.alternativePhone, // FIXME: remove this patch when API gets update
            token: token,
            reCaptchaToken,
            creditKarmaId: subId,
            iovationBlackbox: blackbox,
            mockResponse: mockIC,
            location,
            channel,
            subchannel,
            applicationSubType,
            departmentId,
          },
        });

        setAnalyticsVariablesByApplication(params);

        if (response.ok) {
          const instantCredit: InstantCredit = await response.json();
          dispatch(mergeInstantCredit(instantCredit));
          const {
            isApproved,
            isDeclined,
            hasExistingAccount,
            callId,
            applicationId,
            accountId,
          } = instantCredit;
          setAnalyticsVariables({
            callId: callId,
            applicationId: applicationId,
            accountId: accountId,
          });
          let result = ApplicationResultType.Approved;
          let message = 'Application successfully approved';

          if (hasExistingAccount) {
            handleExistingAccount(instantCredit, { sendToSdk: false });
          } else {
            dispatch(mergeInstantCredit(instantCredit));
          }

          if (isDeclined) {
            result = ApplicationResultType.Declined;
            message = 'Application is declined';
          } else if (!isApproved) {
            result = ApplicationResultType.Pending;
            message = 'Application is pending';
          } else if (hasExistingAccount) {
            result = ApplicationResultType.AccountExists;
            message = 'An existing account exists';
          }

          fmcDispatch(
            receiveApplicationResult({
              result,
              message,
              callId: callId || null,
              status: response.status,
              rewards: instantCredit.rewards,
              applicationId,
              cardType: instantCredit.cardType,
              // logo?
              mobilePhone: params.mobilePhone || undefined,
              alternativePhone: params.alternativePhone || undefined,
              emailAddress: params.emailAddress || undefined,
            }),
          );

          return Promise.resolve(instantCredit);
        } else {
          // Expect this is to be a 4xx error, in which case
          // the server is expected to return an error object.
          // So we'll attempt to parse it and get at the error message,
          // or just return the entire body if it cannot be parsed.

          const body = await response.text();
          let parsedBody;
          try {
            parsedBody = JSON.parse(body);
          } catch (e) {
            /* No need to do anything */
          }

          const message = parsedBody.message;

          if (parsedBody.code) {
            dispatch(mergeInstantCredit({ returnCode: parsedBody.code }));
          }

          fmcDispatch(
            receiveApplicationResult({
              result: ApplicationResultType.Error,
              callId: null,
              message,
              status: response.status,
            }),
          );

          setError(true);
          return Promise.reject(new InstantCreditError());
        }
      } catch (e) {
        if (catchOnly(e, NonClientError)) {
          // Let FmcHost know about server and network errors.
          let status = 0;
          let message = 'NetworkError';

          if (e instanceof ServerError) {
            status = e.response.status;
            message = await e.response.text();
          }

          fmcDispatch(
            receiveApplicationResult({
              result: ApplicationResultType.Error,
              callId: null,
              status: status,
              message: message,
            }),
          );
        }
        // Re-throw so that any exceptions can be handled downstream.
        throw e;
      }
    },
    [
      iovationBlackbox,
      queryStoreNumber,
      defaultStoreNumber,
      fetch,
      token,
      subId,
      mockIC,
      location,
      channel,
      subchannel,
      applicationSubType,
      departmentId,
      setAnalyticsVariablesByApplication,
      dispatch,
      setAnalyticsVariables,
      fmcDispatch,
      handleExistingAccount,
    ],
  );

  return [applyForCredit, status, error];
};

export default useInstantCredit;
